import React, { useContext, useEffect, useState } from "react";
import PageTitle from "../../components/pageTitle/PageTitle";
import { Container, FormControl } from "@material-ui/core";
import {
  Smartphone,
  Monitor,
  User,
  Building,
  Calendar,
  Clock,
  FileText,
  FilePlus2,
  Send,
  ChevronLeft,
  Archive,
  X,
} from "lucide-react";
import { useMediaQuery } from "@mui/material";
import AttachmentsView from "../../components/AttachmentsView/AttachmentsView";
import FileUploadComponent from "../../components/Fields/FileUploadComponent";
import AuthContext from "../../hooks/authContext";
import { packageAccess } from "../../services/helpers";
import { useNavigate, useParams } from "react-router";
import {
  archiveSupportTicket,
  closeSupportTicket,
  getSupportTicketDetails,
  SaveSupportResponse,
  updateSupportStatus,
  uploadAttachment,
} from "../../services/apiservices/support";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textArea";
import moment from "moment";
import ViewAttachments from "../IssueManagement/IssueDetails/ViewAttachments";
import {
  getAllEmployee,
} from "../../services/apiservices/employee";
import CommonContext from "../../hooks/commonContext";
import {
  CommonConfirmationDialog,
  StorageLimitAccess,
} from "../../components/DialogComponent/SupplyDataDialog";
import SingleSelectNew from "../../components/SelectField/SingleSelectNew";
import { useToast } from "../../hooks/useToast";

const ManageSupportDetails = () => {
  const [formdata, setFormData] = useState({ message: "" });
  const commonContext = useContext(CommonContext);
  const isMobile = useMediaQuery("(max-width:767px)");
  const navigate = useNavigate();
  const { supportID } = useParams();
  const [attachments, setAttachments] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [employee, setEmployee] = useState();
  const [supports, setSupports] = useState();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmingCloseAction, setConfirmingCloseAction] = useState(false);
  const [confirmingArchiveAction, setConfirmingArchiveAction] = useState(false);
  const auth = useContext(AuthContext);
  const companyId =
    auth?.userProfile?.role === "companyUser"
      ? auth?.userProfile?.id
      : auth?.userProfile?.companyId;
  const userPackageId = auth?.userProfile?.prdId;
  const userPromocode = auth?.userProfile?.userPromoCode || auth?.userProfile?.companyData?.userPromoCode;
  const subscriptionId = auth?.userProfile?.subscriptionDetails?.scheduleId || auth?.userProfile?.subscriptions?.data?.[0]?.scheduleId;
  const allowedPermissions = packageAccess(userPackageId, userPromocode, subscriptionId);
  const { toast } = useToast();

  const statusOptions = [
    { label: "Open", value: "open" },
    { label: "Pending", value: "pending" },
    { label: "Resolved", value: "resolved" },
    { label: "Close", value: "close" },
  ];

  const fetchData = async () => {
    try {
      commonContext?.setLoader(true);

      const [employeeRes, ticketRes] = await Promise.all([
        new Promise((resolve, reject) => getAllEmployee(resolve, reject)),
        new Promise((resolve) => getSupportTicketDetails(supportID, resolve)),
      ]);

      const employeeList = employeeRes?.data || [];
      const ticket = ticketRes?.data;

      const employeeMap = new Map(
        employeeList?.map((emp) => [
          emp?.id,
          {
            name: `${emp?.firstName} ${emp?.lastName}`,
            email: emp?.email || "",
            role: emp?.role,
            id: emp?.id
          },
        ])
      );

      const responses = Object.values(ticket?.responses || {});
      const latestResponse = responses.reduce(
        (a, b) => (a?.respondOn > b?.respondOn ? a : b),
        {}
      );

      const creator = employeeMap?.get(ticket?.createdBy);

      const companyInfo = employeeList?.find(
        (item) => item?.id === ticket?.companyId
      );
      const companyPlan =
        companyInfo?.subscriptions?.data?.length > 0
          ? companyInfo.subscriptions.data[0]?.plan?.name
          : "Trial Plan";

      const enriched = {
        ...ticket,
        creator: creator,
        user: creator?.name || "Unknown",
        userEmail: creator?.email || "Unknown",
        respondedByName:
          responses.length > 0 ? latestResponse?.respondBy?.name || "Unknown" : "No Response",
        company: companyPlan,
        role: latestResponse?.respondBy?.role,
      };

      if (ticketRes?.status) {
        setEmployee(employeeList);
        setSupports(enriched);
      } else {
        console.error("Error fetching ticket:", ticketRes?.error);
        setSupports(undefined);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setSupports(undefined);
    } finally {
      commonContext?.setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportID]); // fetchData is stable, no need to include in deps

  const handlefieldChange = (name, value) => {
    setFormData({ ...formdata, [name]: value });
  };

  const onFileUpload = async (files) => {
    const extraLimit = auth?.userProfile?.additionalQuantity * 5;
    const extraLimitInBytes = extraLimit * 1024 ** 3;
    const storageLimit =
      (allowedPermissions?.storageLimits ?? 0) + extraLimitInBytes;

    let validFiles = [];

    files?.forEach((file) => {
      if (auth?.userProfile?.storage + file.size > storageLimit) {
        setDialogOpen(true);
      } else {
        validFiles.push(file);
      }
    });

    setAttachments((prevAttachments) => {
      const newAttachments = [...prevAttachments, ...validFiles];
      if (newAttachments.length > 5) {
        return prevAttachments;
      }
      return newAttachments;
    });
  };

  const handleDelete = (fileToDelete) => {
    setAttachments((prevAttachments) => {
      return prevAttachments.filter((file) => file !== fileToDelete);
    });
  };

  const onSaveChannel = async () => {
    try {
      commonContext?.setLoader(true);
      let attachmentUrls = [];

      if (attachments.length > 0) {
        attachmentUrls = await Promise.all(
          attachments.map((file) => uploadAttachment(companyId, file))
        );
      }

      const data = {
        response: formdata?.message,
        attachments: attachmentUrls,
      };

      const details = {
        id: auth?.userProfile?.id,
        name : `${auth?.userProfile?.firstName} ${auth?.userProfile?.lastName}`,
        role :  Array.isArray(auth?.userProfile?.role) ? (auth.userProfile.role.includes("admin") ? "admin" : auth.userProfile.role[0]) : auth?.userProfile?.role
      }
      const companyName = `${auth?.userProfile?.companyData?.data?.companyName}`

      const userData = supports?.creator;
      const res= await SaveSupportResponse(supportID, data, details, userData, companyName);

      if (res?.status === true) {
        setFormData({ message: "" });
        setAttachments([]);
        toast?.({title: "Success", description: "Comment Added Successfully"});
      }
      commonContext?.setLoader(false);
      fetchData();
    } catch (error) {
      console.error("Failed to save support ticket:", error);
      commonContext?.setLoader(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    await updateSupportStatus(supportID, status);
    await fetchData();
     toast?.({title: "Success", description: `Support Ticket Status Update Successfully`});
  };

  const handleConfirmArchive = async () => {
    setConfirmingArchiveAction(false);
    await archiveSupportTicket(supportID);
    navigate("/help/manageSupport");
    toast?.({title: "Success", description: `Support Ticket Archive Successfully`});
  };

  const handleConfirmClose = async () => {
    setConfirmingCloseAction(false);
    await closeSupportTicket(supportID);
    await fetchData();
    toast?.({title: "Success", description: `Support Ticket Close Successfully`});
  };

  const handleRedirectToBack = () => {
    navigate("/help/manageSupport");
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  const handleClostTicketDialogClose = () => {
    setConfirmingCloseAction(false);
  };
  const handleArchiveTicketDialogClose = () => {
    setConfirmingArchiveAction(false);
  };

  const TicketStatusBadge = ({ status }) => {
    const getVariant = () => {
      switch (status) {
        case "open":
          return { variant: "default", text: "Open" };
        case "pending":
          return { variant: "warning", text: "Pending" };
        case "resolved":
          return { variant: "success", text: "Resolved" };
        case "close":
          return { variant: "outline", text: "Closed" };
        default:
          return { variant: "default", text: status };
      }
    };

    const { variant, text } = getVariant();

    return (
      <Badge
        variant={variant}
        className="min-w-[80px] text-center justify-center"
      >
        {text}
      </Badge>
    );
  };

 const isSuperUser = Array.isArray(auth?.userProfile?.role) ? auth?.userProfile.role.includes("superUser") : auth?.userProfile?.role === "superUser";

  return (
    <>
      <Container>
        <PageTitle
          pageTitle={`Ticket : ${supports?.subject}`}
          showBreadcrumb={
            isSuperUser ? [{ label: "Home", to: "/superuserdashboard" }, { label: "Support", to: "/help/manageSupport" },] : [{ label: "Home", to: "/" }, { label: "Support", to: "/help/manageSupport" },]
          }
          BreadCrumbCurrentPageTitle={`Ticket T-${supports?.ticketNumber}`}
          showLastSeen={true}
        />
        <div className={isMobile ? "pv-16 ph-6" : "pv-24 ph-32"}>
          <div className={isMobile ? "mb-24" : "mb-16"}>
            <Button
              variant="outline" size="sm" className="flex items-center gap-1 w-full sm:w-fit"
              onClick={handleRedirectToBack}
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Tickets
            </Button>
          </div>
          {isSuperUser && (
            <div
              className={
                isMobile ? "d-flex flex-direction-column" : "d-flex flex-center"
              }
            >
              <span className="text-sm font-medium mr-5 dark:text-white">Status:</span>
              <div
                className={
                  isMobile
                    ? "d-flex flex-direction-column"
                    : "d-flex flex-center"
                }
              >
                <div>
                  <SingleSelectNew
                    contentClass="bg-white text-black border-gray-300 dark:bg-[#2c3440] dark:text-white dark:border-[#464c59]"
                    itemClass="hover:bg-gray-100 dark:hover:bg-[#3c4351]"
                    placeholder={"selectStatus"}
                    value={supports?.status}
                    onChange={handleUpdateStatus}
                    options={statusOptions}
                  />
                </div>
                {supports?.status !== "close" && (
                  <div className={isMobile ? "mv-10" : "mh-10"}>
                    <Button
                      variant="outline"
                      onClick={() => setConfirmingCloseAction(true)}
                      style={{
                        width: isMobile ? "410px" : "",
                      }}
                    >
                      <X className="h-4 w-4 mr-4" /> Close Ticket
                    </Button>
                  </div>
                )}
                {supports?.status === "close" && (
                  <div className={isMobile ? "mv-10" : "mh-10"}>
                    <Button
                      variant="outline"
                      onClick={() => setConfirmingArchiveAction(true)}
                      style={{
                        width: isMobile ? "410px" : "",
                      }}
                    >
                      <Archive className="h-4 w-4 mr-4" /> Archive Ticket
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            className={isMobile ? "sectionBox pv-15 ph-10 dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]" : "sectionBox p-24 dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]"}
            style={{
              // border: "1px solid hsl(214.3, 31.8%, 91.4%)",
              // borderRadius: "8px",
            }}
          >
            <Card className="dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
              <CardContent className="p-16">
                <div className="mb-12">
                  <h1 className="text-xl font-bold">{supports?.subject}</h1>
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <TicketStatusBadge status={supports?.status} />
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-800">
                      {supports?.portal === "web" ? "Portal" : "Mobile"}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                      {supports?.type === "technical" ? "Technical" : "General"}
                    </span>
                    <div className="flex items-center text-xs text-gray-500">
                      {supports?.portal === "Mobile App" ? (
                        <Smartphone className="h-3 w-3 mr-4" />
                      ) : (
                        <Monitor className="h-3 w-3 mr-4" />
                      )}
                      <span>
                        {supports?.portal === "web" ? "Web App" : "Mobile App"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <User className="h-4 w-4 mr-8 text-gray-500" />
                      <span>
                        <strong className="dark:text-gray-100">From:</strong> {supports?.user} (
                        {supports?.userEmail})
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Building className="h-4 w-4 mr-8 text-gray-500" />
                      <span>
                        <strong className="dark:text-gray-100">Account Type:</strong> {supports?.company}
                      </span>
                    </div>
                    {supports?.respondedByName && (
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="h-4 w-4 mr-8 text-gray-500" />
                        <span>
                          <strong className="dark:text-gray-100">Last Responded By:</strong>
                          <span
                            className={`ml-4`}
                            style={{
                              color:
                                supports?.role === "superUser" ? "#2563EB" : "",
                              fontWeight:
                                supports?.role === "superUser" ? "500" : "",
                              fontSize:
                                supports?.role === "superUser" ? "14px" : "",
                            }}
                          >
                            {supports?.respondedByName}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-8 text-gray-500" />
                      <span>
                        <strong className="dark:text-gray-100">Created:</strong>{" "}
                        {moment
                          .unix(supports?.createdAt.seconds)
                          .format("MMM D, YYYY h:mm A")}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="h-4 w-4 mr-8 text-gray-500" />
                      <span>
                        <strong className="dark:text-gray-100">Last Updated:</strong>{" "}
                        {moment
                          .unix(supports?.updatedAt.seconds)
                          .format("MMM D, YYYY h:mm A")}
                      </span>
                    </div>
                    {supports?.attachments &&
                      supports?.attachments?.length > 0 && (
                        <div className="flex items-center text-sm text-gray-600">
                          <FileText className="h-4 w-4 mr-8 text-gray-500" />
                          <span>
                            <strong className="dark:text-gray-100">Attachments:</strong>{" "}
                            {supports?.attachments?.length}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-32 dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
              <CardHeader className="p-24">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-base font-medium flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {supports?.user}
                    </CardTitle>
                    <p className="text-sm text-gray-500">
                      {moment
                        .unix(supports?.createdAt.seconds)
                        .format("MMM D, YYYY h:mm A")}
                    </p>
                  </div>
                  {supports?.portal && (
                    <div className="text-xs px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                      {supports?.portal === "web" ? "Portal" : "Mobile"}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-24 ph-24">
                <p
                  style={{ fontSize: "16px" }}
                  className="whitespace-pre-line"
                >
                  {supports?.description}
                </p>

                {supports?.attachments && supports?.attachments.length > 0 && (
                  <div className="mt-16">
                    <p className="text-sm font-medium text-gray-700 mb-8">
                      Attachments:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <div className="flex items-center rounded-md bg-slate-100 p-8 text-sm">
                        <FileText className="h-4 w-4 mr-8 text-slate-500" />
                        <ViewAttachments attachments={supports?.attachments} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-16 text-sm text-gray-500">
                  <p className="flex items-center gap-1.5">
                    Last Update:{" "}
                    {moment
                      .unix(supports?.updatedAt.seconds)
                      .format("MMM D, YYYY h:mm A")}
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="mt-32">
              <h2
                className="mb-16 text-lg font-medium text-foreground"
              >
                Comment History
              </h2>
              {supports?.responses &&
                Object.values(supports.responses)
                  .sort((a, b) => b.respondOn - a.respondOn)
                  .map((responseItem, index) => {

                    return (
                      <Card
                        key={index}
                        className="mt-32 dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]"
                        style={{
                          // border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                          // borderRadius: "8px",
                          // boxShadow: "none",
                        }}
                      >
                        <CardContent className="ph-24 pv-24">
                          <div className="d-flex flex-space-between">
                            <div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "500",
                                }}
                              >
                                {responseItem?.respondBy?.name}
                              </div>
                              <p
                                className="mt-8 mb-0"
                                style={{ color: "#6B7280" }}
                              >
                                {moment(responseItem?.respondOn).format(
                                  "MMM D, YYYY h:mm A"
                                )}
                              </p>
                            </div>

                            {responseItem?.attachments?.length > 0 && (
                              <div style={{ width: isMobile ? "40%" : "" }}>
                                <ViewAttachments
                                  attachments={responseItem?.attachments}
                                />
                              </div>
                            )}
                          </div>
                        </CardContent>
                        <div
                          className="ph-24 pt-0 pb-24"
                          style={{ fontSize: "16px"}}
                        >
                          {responseItem?.response}
                        </div>
                      </Card>
                    );
                  })}
            </div>

            <div className="mt-32">
              <h2
                className="mb-16 text-lg font-medium text-foreground"
              >
                Add Comment
              </h2>
              <div>
                <Textarea
                  value={formdata?.message ? formdata?.message : ""}
                  onChange={(e) => handlefieldChange("message", e.target.value)}
                  id="message"
                  className="min-h-[120px] mb-3"
                  placeholder="Enter your response..."
                />
              </div>
              <div className="mb-20 mt-10 d-flex flex-space-between flex-center">
                <FormControl fullWidth className="mv-15">
                  <AttachmentsView
                    attachments={attachments}
                    onDelete={handleDelete}
                  />
                  <FileUploadComponent
                    attachments={attachments}
                    onuploadfile={onFileUpload}
                    onlyImages={true}
                    buttonText={
                      <span className="d-flex flex-center">
                        <FilePlus2 className="h-4 w-4 mr-4" /> Attach Files
                      </span>
                    }
                  />
                </FormControl>
                <div style={{ width: isMobile ? "200px" : "150px" }}>
                  <Button
                    className="d-flex flex-center"
                    style={{
                      color: "white",
                      backgroundColor:
                        formdata?.message === "" ? "gray" : "#157F3c",
                    }}
                    disabled={formdata?.message === ""}
                    onClick={onSaveChannel}
                  >
                    <Send className="h-4 w-4 mr-4" />
                    <span>Submit Reply</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
      {dialogOpen && (
        <StorageLimitAccess
          open={dialogOpen}
          onClose={handleDialogClose}
          auth={auth}
        />
      )}
      {confirmingCloseAction && (
        <CommonConfirmationDialog
          open={confirmingCloseAction}
          onConfirm={handleConfirmClose}
          onClose={handleClostTicketDialogClose}
          title={"Close Ticket"}
          message={"Are you sure you want to close this ticket? This will mark the ticket as resolved and remove it from the active list."}
          buttonTitle={"Yes, Close ticket"}
        />
      )}
      {confirmingArchiveAction && (
        <CommonConfirmationDialog
          open={confirmingArchiveAction}
          onConfirm={handleConfirmArchive}
          onClose={handleArchiveTicketDialogClose}
          title={"Archive Ticket"}
          message={"Are you sure you want to archive this ticket? This action will move the ticket to the archive and cannot be undone."}
          buttonTitle={"Yes, archive ticket"}
        />
      )}
    </>
  );
};

export default ManageSupportDetails;
